/**
 * PlaceholderPage Component
 * AIDLC Administrative Backend
 *
 * Reusable "Coming Soon" placeholder for features that haven't been built yet.
 * Displays the feature name, a brief description, and the expected persona audience.
 *
 * Used by: Budget Proposals, Agent Scheduler, Token Roadmap, Token Estimation,
 * Assessment Reports, Milestone Tracker, Spawn Planner, Budget Burn Monitor,
 * Escalation Queue, DoD Pipeline.
 */

import React from 'react';

interface PlaceholderPageProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  personas?: string[];
}

export function PlaceholderPage({ title, description, icon, personas }: PlaceholderPageProps) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center max-w-md">
        {/* Icon */}
        <div className="mx-auto w-16 h-16 rounded-2xl bg-ldaja-blue/10 flex items-center justify-center text-ldaja-blue mb-6">
          {icon}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-ldaja-black mb-2">{title}</h1>

        {/* Coming Soon Badge */}
        <span className="inline-block px-3 py-1 text-xs font-semibold text-ldaja-blue bg-ldaja-blue/10 rounded-full mb-4">
          Coming Soon
        </span>

        {/* Description */}
        <p className="text-sm text-gray-500 leading-relaxed mb-6">{description}</p>

        {/* Persona Tags */}
        {personas && personas.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs text-gray-400">Available to:</span>
            {personas.map((p) => (
              <span
                key={p}
                className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-full"
              >
                {p}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
